/**
 * usePresentationSync
 *
 * Thin wrapper around the BroadcastChannel API for syncing the
 * "Presentera med presenter view" dual-window experience. The master
 * (audience) window and the presenter window both open a channel
 * named `slide-sense-present:<sessionId>` and exchange small JSON
 * messages over it.
 *
 * Feature detection:
 *   - BroadcastChannel is available in all modern browsers (Edge 79+,
 *     Chrome 54+, Firefox 38+, Safari 15.4+). If it is missing we
 *     return no-op publish/subscribe so both windows still load; the
 *     presenter view simply won't be kept in sync with the audience
 *     window. This is a v1 trade-off — Task 5 plan explicitly chose to
 *     skip a localStorage / postMessage fallback.
 *
 * Channel lifetime:
 *   - The channel is opened when a non-null `sessionId` is passed and
 *     closed automatically on unmount (or when `sessionId` becomes
 *     null). Callers do NOT need to manage the channel themselves.
 *
 * Message contract:
 *   - `{type: "hello"}`  — sent by the presenter on mount (after it has
 *     wired up its subscribe) to ask the master to (re)broadcast an
 *     `init`. Guards against the race where the master's first-mount
 *     `init` is published before the presenter window subscribes —
 *     BroadcastChannel does not buffer, so that message would otherwise
 *     be lost and the presenter would hang on "Väntar på presentationen…".
 *     The master responds to every `hello`, including repeats, so the
 *     presenter can be closed and reopened freely.
 *   - `{type: "init"}`   — sent by master once after mount so the
 *     presenter window can hydrate its initial state.
 *   - `{type: "state"}`  — sent on every slide-change / mode-change so
 *     the other side stays in sync.
 *   - `{type: "jump"}`   — sent by the presenter when the user uses
 *     presenter-window controls / keyboard; master picks it up and
 *     advances its local state (which then broadcasts `state`).
 *   - `{type: "closed"}` — sent on window unload so the peer can show
 *     a "presentation ended" indicator.
 */

import { useEffect, useMemo, useRef } from "react"

import type { Stroke } from "./AnnotationCanvas"
import type { BuilderSlide } from "../../types"

// ---------------------------------------------------------------------------
// Public message types
// ---------------------------------------------------------------------------

/** High-level mode of the master presentation window. */
export type PresentationSyncMode = "nav" | "laser" | "pen" | "blank"

export type PresentationSyncMessage =
  | {
      type: "hello"
    }
  | {
      type: "init"
      slides: BuilderSlide[]
      currentIndex: number
      mode: PresentationSyncMode
      /** PowerPoint-parity return target for last_viewed hotspots (dual-window). */
      lastViewedIndex: number | null
    }
  | {
      type: "state"
      currentIndex: number
      mode: PresentationSyncMode
      /** PowerPoint-parity return target for last_viewed hotspots (dual-window). */
      lastViewedIndex: number | null
    }
  | {
      type: "jump"
      index: number
      /** PowerPoint-parity return target for last_viewed hotspots (dual-window). */
      lastViewedIndex: number | null
    }
  | {
      type: "closed"
    }
  // --- Presenter → audience tool mirroring (#2153) -------------------------
  // The presenter window drives the audience's laser / pen / blank from its
  // own screen. Coordinates are normalized 0..1 relative to the slide image
  // box (the "stage"), so they line up across the two differently-shaped
  // windows. The audience applies these events; it never echoes them back.
  | {
      // Laser dot moved — normalized image-box coordinates.
      type: "laserMove"
      x: number
      y: number
    }
  | {
      // Laser turned off / pointer left the slide — hide the dot.
      type: "laserOff"
    }
  | {
      // A completed pen stroke for `slideId` (points in image-box coords).
      type: "stroke"
      slideId: string
      stroke: Stroke
    }
  | {
      // Clear all strokes on `slideId`.
      type: "clearStrokes"
      slideId: string
    }
  | {
      // Undo the last stroke on `slideId`.
      type: "undoStroke"
      slideId: string
    }
  | {
      // Blank / unblank the audience screen (presenter keeps seeing slides).
      type: "blank"
      blank: boolean
    }
  | {
      // End the whole presentation (presenter pressed Exit/Esc).
      type: "end"
    }

export interface PresentationSyncApi {
  /** Broadcast a message to the peer window. No-op when unsupported. */
  publish: (msg: PresentationSyncMessage) => void
  /**
   * Register a listener for incoming messages. Returns an unsubscribe
   * function. Safe to call multiple times — listeners are managed
   * independently, not stored on the channel itself.
   */
  subscribe: (listener: (msg: PresentationSyncMessage) => void) => () => void
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const CHANNEL_PREFIX = "slide-sense-present:"

/**
 * Establish (and manage the lifetime of) a BroadcastChannel tied to a
 * presentation session. Pass `null` to disable — useful for the
 * single-window flow where no channel is needed.
 */
export function usePresentationSync(sessionId: string | null): PresentationSyncApi {
  // Use a ref for the channel so the identity of the returned API is
  // stable across renders (consumers can safely depend on it in
  // useEffect dependency arrays without retrying on every render).
  const channelRef = useRef<BroadcastChannel | null>(null)
  const listenersRef = useRef<Set<(msg: PresentationSyncMessage) => void>>(new Set())

  useEffect(() => {
    // Don't clear listenersRef here: React runs child effects before parent
    // effects on mount, so consumers (e.g. PresenterView) have already
    // registered their subscribers by the time this effect runs. Clearing
    // would silently drop them and leave the channel dispatching to no one.
    // Listener cleanup happens on unmount via the return below.

    if (!sessionId) return
    if (typeof BroadcastChannel === "undefined") return

    const channel = new BroadcastChannel(`${CHANNEL_PREFIX}${sessionId}`)
    channelRef.current = channel

    const onMessage = (event: MessageEvent<PresentationSyncMessage>) => {
      // Copy the set before iterating so a listener that unsubscribes
      // itself doesn't mutate the collection during traversal.
      const snapshot = Array.from(listenersRef.current)
      for (const listener of snapshot) {
        try {
          listener(event.data)
        } catch (err) {
          // A misbehaving listener shouldn't break the others.
          console.error("Presentation sync listener threw", err)
        }
      }
    }

    channel.addEventListener("message", onMessage)

    return () => {
      channel.removeEventListener("message", onMessage)
      channel.close()
      channelRef.current = null
      listenersRef.current.clear()
    }
  }, [sessionId])

  return useMemo<PresentationSyncApi>(
    () => ({
      publish: (msg) => {
        const ch = channelRef.current
        if (!ch) return
        try {
          ch.postMessage(msg)
        } catch (err) {
          // postMessage throws on un-cloneable payloads — log but don't
          // crash the UI; the caller can retry if it matters.
          console.error("Presentation sync publish failed", err)
        }
      },
      subscribe: (listener) => {
        listenersRef.current.add(listener)
        return () => {
          listenersRef.current.delete(listener)
        }
      },
    }),
    // sessionId changes trigger the effect above which recreates the
    // channel; the returned API object itself can stay stable because
    // it reads the ref lazily.
    []
  )
}
